import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { message } from 'antd';
import AuthService, { User, LoginRequest, RegisterRequest } from '../services/auth';
import { getApiResponseErrorMessage, getAxiosErrorMessage } from '../utils/error';

// Auth state interface
interface AuthState {
  user: User | null;
  permissions: string[];
  roles: string[];
  loading: boolean;
  authenticated: boolean;
}

// Auth actions
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; permissions: string[]; roles: string[] } }
  | { type: 'SET_AUTH_DATA'; payload: { user: User; token: string; authenticated: boolean } }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User };

// Auth context interface
interface AuthContextType {
  state: AuthState;
  login: (credentials: LoginRequest) => Promise<boolean>;
  register: (userData: RegisterRequest) => Promise<boolean>;
  logout: () => void;
  updateProfile: (userData: Partial<Pick<User, 'username' | 'display_name' | 'email'>>) => Promise<boolean>;
  updateUserInfo?: (userData: Partial<User>) => void;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  setAuthData?: (data: { user: User; token: string; authenticated: boolean }) => void;
}

// Initial state
const initialState: AuthState = {
  user: null,
  permissions: [],
  roles: [],
  loading: true,
  authenticated: false,
};

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        permissions: action.payload.permissions,
        roles: action.payload.roles,
        loading: false,
        authenticated: true,
      };
    case 'SET_AUTH_DATA':
      return {
        ...state,
        user: action.payload.user,
        loading: false,
        authenticated: action.payload.authenticated,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        permissions: [],
        roles: [],
        loading: false,
        authenticated: false,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      if (AuthService.isAuthenticated()) {
        try {
          const response = await AuthService.me();
          if (response.success) {
            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: {
                user: response.data.user,
                permissions: response.data.permissions,
                roles: response.data.roles,
              },
            });
          } else {
            AuthService.removeToken();
            dispatch({ type: 'SET_LOADING', payload: false });
          }
        } catch (error) {
          console.error('Failed to initialize auth:', error);
          AuthService.removeToken();
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (credentials: LoginRequest): Promise<boolean> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await AuthService.login(credentials);

      if (response.success && response.data) {
        AuthService.setToken(response.data.access_token);

        // Get full user data including permissions
        const meResponse = await AuthService.me();
        if (meResponse.success) {
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: {
              user: meResponse.data.user,
              permissions: meResponse.data.permissions,
              roles: meResponse.data.roles,
            },
          });
          message.success('登录成功！');
          return true;
        }
      } else {
        message.error(getApiResponseErrorMessage(response, '登录失败'));
        dispatch({ type: 'SET_LOADING', payload: false });
        return false;
      }
    } catch (error: any) {
      console.error('Login error:', error);
      message.error(getAxiosErrorMessage(error, '登录失败'));
      dispatch({ type: 'SET_LOADING', payload: false });
      return false;
    }
    return false;
  };

  // Register function
  const register = async (userData: RegisterRequest): Promise<boolean> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await AuthService.register(userData);

      if (response.success && response.data) {
        AuthService.setToken(response.data.access_token);

        // Get full user data including permissions
        const meResponse = await AuthService.me();
        if (meResponse.success) {
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: {
              user: meResponse.data.user,
              permissions: meResponse.data.permissions,
              roles: meResponse.data.roles,
            },
          });
          message.success('注册成功！');
          return true;
        }
      } else {
        message.error(getApiResponseErrorMessage(response, '注册失败'));
        dispatch({ type: 'SET_LOADING', payload: false });
        return false;
      }
    } catch (error: any) {
      console.error('Register error:', error);
      message.error(getAxiosErrorMessage(error, '注册失败'));
      dispatch({ type: 'SET_LOADING', payload: false });
      return false;
    }
    return false;
  };

  // Logout function
  const logout = async () => {
    try {
      await AuthService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      AuthService.removeToken();
      dispatch({ type: 'LOGOUT' });
      message.success('已退出登录');
    }
  };

  // Update profile function
  const updateProfile = async (userData: Partial<Pick<User, 'username' | 'display_name' | 'email'>>): Promise<boolean> => {
    try {
      const response = await AuthService.updateProfile(userData);

      if (response.success && response.data) {
        dispatch({ type: 'UPDATE_USER', payload: response.data.user });
        message.success('个人信息更新成功！');
        return true;
      } else {
        message.error(getApiResponseErrorMessage(response, '更新失败'));
        return false;
      }
    } catch (error: any) {
      console.error('Update profile error:', error);
      message.error(getAxiosErrorMessage(error, '更新失败'));
      return false;
    }
  };

  // Permission checking functions
  const hasPermission = (permission: string): boolean => {
    // 管理员（admin）拥有所有权限
    if (state.roles.includes('admin')) {
      return true;
    }

    return state.permissions.includes(permission);
  };

  const hasRole = (role: string): boolean => {
    return state.roles.includes(role);
  };

  const hasAnyPermission = (permissions: string[]): boolean => {
    // 管理员（admin）拥有所有权限
    if (state.roles.includes('admin')) {
      return true;
    }
    return permissions.some(permission => state.permissions.includes(permission));
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some(role => state.roles.includes(role));
  };

  // Set auth data function
  const setAuthData = (data: { user: User; token: string; authenticated: boolean }) => {
    // 先写入令牌，保证后续请求携带
    AuthService.setToken(data.token);

    // 立即设置基本用户态，避免界面空白
    dispatch({
      type: 'SET_AUTH_DATA',
      payload: data,
    });

    // 异步刷新权限/角色
    AuthService.me()
      .then((res) => {
        if (res.success) {
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: {
              user: res.data.user,
              permissions: res.data.permissions,
              roles: res.data.roles,
            },
          });
        }
      })
      .catch((err) => {
        console.error('Refresh auth after setAuthData failed:', err);
      });
  };

  // Update user info function
  const updateUserInfo = (userData: Partial<User>) => {
    if (state.user) {
      dispatch({
        type: 'UPDATE_USER',
        payload: { ...state.user, ...userData },
      });
    }
  };

  const value: AuthContextType = {
    state,
    login,
    register,
    logout,
    updateProfile,
    updateUserInfo,
    hasPermission,
    hasRole,
    hasAnyPermission,
    hasAnyRole,
    setAuthData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
